import { Fragment, FunctionComponent, memo, useState } from "react";
import { Box, BoxContent, BoxHeader, BoxHeading, BoxSubheading, useQueryParameters } from "@edgetier/client-components";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { SpinnerUntil } from "@edgetier/components";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useAgentFirstName } from "hooks-for/agents/use-agent-first-name";
import useInteractionsCounts from "hooks-for/interactions/use-interactions-counts";
import { formatDateTickValue } from "utilities/format-date-tick-value";
import AgentInteractionsVolumeChartFilters from "./agent-interactions-volume-chart-filters";
import { IFormValues } from "./agent-interactions-volume-chart-filters/agent-interactions-volume-chart-filters.types";
import { MAXIMUM_VOLUME_POINTS } from "./agent-interactions-volume-chart.constants";
import { IInteractionsCount, TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { upperXAxisTickInterval } from "components-for/anomalies/phrase-counts-chart/phrase-counts-chart.utilities";
import { IProps } from "./agent-interactions-volume-chart.types";
import { dateTimeUpperXAxisTickFormatter } from "utilities-for/charts/date-time-upper-x-axis-tick-formatter";
import PhraseCountsChartTooltip from "components-for/anomalies/phrase-counts-chart/phrase-counts-chart-tooltip";
import "./agent-interactions-volume-chart.scss";

/**
 * Show a bar chart with agent total interactions.
 * @param props.defaultEndDateTime   Closing date in search range.
 * @param props.defaultStartDateTime Opening date in search range.
 * @param props.height               Optional height (mainly for testing).
 * @param props.width                Optional width (mainly for testing).
 */
const AgentInteractionsVolumeChart: FunctionComponent<IProps> = ({
    defaultStartDateTime,
    defaultEndDateTime,
    height = "100%",
    width = "100%",
}) => {
    const queryFilters = useInteractionQueryParameters({
        defaults: {
            startDateTime: defaultStartDateTime,
            endDateTime: defaultEndDateTime,
        },
    });

    const [formFilters, setFormFilters] = useState<IFormValues>({
        timePeriodGroupBy: TimePeriodGroupBy.Days,
    });

    const { reviewAgentId } = useQueryParameters({ reviewAgentId: String });

    // Make sure to only send the review agent in the request.
    const filters = { ...queryFilters, agentId: reviewAgentId, groupBy: formFilters.timePeriodGroupBy };
    const { data: agentInteractionsCount, isLoading: isAgentInteractionsCountLoading } =
        useInteractionsCounts<IInteractionsCount<Date>[]>(filters);

    // Get the agent's name for the title etc.
    const { data: agentFirstName, isLoading: isAgentFirstNameLoading } = useAgentFirstName(reviewAgentId);

    /**
     * Tick formatter for the second X axis
     */
    const lowerXAxisTickFormatter = (tickValue: any, dataLength: number, timePeriodGroup: TimePeriodGroupBy) => {
        const { x, y, payload } = tickValue;
        const { offset, value } = payload;

        if (timePeriodGroup === TimePeriodGroupBy.Minutes) {
            // Transform to Date
            const minute = new Date(value).getMinutes();

            // Display label in the middle of each hour.
            if (minute === 30) {
                return (
                    <text x={x} y={y} textAnchor="middle">
                        {formatDateTickValue(value, TimePeriodGroupBy.Hours)}
                    </text>
                );
            }

            // Vertical line to separate days.
            if (minute === 59 && payload.index !== dataLength - 1) {
                const pathX = Math.floor(x + offset) + 0.5;
                return <path d={`M${pathX},${y + 3}v-15`} />;
            }
            return <Fragment />;
        }

        if (timePeriodGroup === TimePeriodGroupBy.Hours) {
            // Transform to Date
            const hour = new Date(value).getHours();

            // Display label in the middle of each day.
            if (hour === 12) {
                return (
                    <text x={x} y={y} textAnchor="middle">
                        {formatDateTickValue(value, TimePeriodGroupBy.Days)}
                    </text>
                );
            }

            // Vertical line to separate days.
            if (hour === 23 && payload.index !== dataLength - 1) {
                const pathX = Math.floor(x + offset) + 0.5;
                return <path d={`M${pathX},${y + 3}v-15`} />;
            }
            return <Fragment />;
        }

        return <Fragment />;
    };

    return (
        <Box className="agent-interactions-volume-chart">
            <BoxHeader>
                <div>
                    <BoxHeading>
                        {isAgentFirstNameLoading && <>&hellip;</>}
                        {!isAgentFirstNameLoading && <>Interactions Count</>}
                    </BoxHeading>
                    <BoxSubheading>
                        {isAgentFirstNameLoading && <>&hellip;</>}
                        {!isAgentFirstNameLoading && (
                            <>
                                Interactions handled by {agentFirstName} per {formFilters.timePeriodGroupBy}
                            </>
                        )}
                    </BoxSubheading>
                </div>
                <AgentInteractionsVolumeChartFilters initialValues={formFilters} onChange={setFormFilters} />
            </BoxHeader>
            <BoxContent>
                <SpinnerUntil<IInteractionsCount<Date>[]>
                    data={agentInteractionsCount}
                    isReady={!isAgentInteractionsCountLoading}
                >
                    {(data) =>
                        Array.isArray(data) && data.length > MAXIMUM_VOLUME_POINTS ? (
                            <div className="agent-interactions-volume-chart__excess">Too much data to display</div>
                        ) : (
                            <ResponsiveContainer
                                height={height}
                                width={width}
                                className="agent-interactions-volume-chart__container"
                            >
                                <BarChart data={data}>
                                    <CartesianGrid />
                                    <Bar dataKey="count" radius={5} />
                                    <XAxis
                                        xAxisId={0}
                                        interval={upperXAxisTickInterval(formFilters.timePeriodGroupBy)}
                                        dataKey="group"
                                        tickFormatter={(tickValue) =>
                                            dateTimeUpperXAxisTickFormatter(
                                                defaultStartDateTime,
                                                tickValue,
                                                formFilters.timePeriodGroupBy
                                            )
                                        }
                                        tick={{
                                            fontSize:
                                                formFilters.timePeriodGroupBy === TimePeriodGroupBy.Minutes ||
                                                formFilters.timePeriodGroupBy === TimePeriodGroupBy.Hours
                                                    ? 10
                                                    : undefined,
                                        }}
                                    />

                                    {formFilters.timePeriodGroupBy !== TimePeriodGroupBy.Months && (
                                        <XAxis
                                            xAxisId={1}
                                            axisLine={false}
                                            dataKey="group"
                                            scale="band"
                                            interval={0}
                                            tick={(tickValue) =>
                                                lowerXAxisTickFormatter(
                                                    tickValue,
                                                    data.length,
                                                    formFilters.timePeriodGroupBy
                                                )
                                            }
                                        />
                                    )}

                                    <YAxis tickFormatter={(value) => value} />
                                    <Tooltip
                                        isAnimationActive={false}
                                        content={({ active, payload }) =>
                                            active &&
                                            Array.isArray(payload) &&
                                            typeof payload[0].value === "number" && (
                                                <PhraseCountsChartTooltip
                                                    startDateTime={filters.startDateTime!}
                                                    endDateTime={filters.endDateTime!}
                                                    dateTime={payload[0].payload.group.getTime()}
                                                    proportion={false}
                                                    timePeriodGroup={filters.groupBy}
                                                    value={payload[0].payload.count}
                                                />
                                            )
                                        }
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        )
                    }
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(AgentInteractionsVolumeChart);
